/**
 * Copy helpers for UpgradeModal, split out so they can be unit-tested without
 * mounting the dialog (repo "Simple Component Pattern").
 *
 * The modal is the front door to /subscription — its "Learn More" button routes
 * straight there (VerticalMenuBar.handleLearnMore). It therefore draws its plan
 * copy from the SAME catalog the subscription cards use
 * (`@/config/onboardPricingCatalog`) instead of hardcoding per-plan blurbs in
 * the template, so a merchant is not sold one thing in the modal and another on
 * the page they land on.
 *
 * The impedance mismatch this module bridges: menu `lockInfo` carries a plan
 * DISPLAY NAME ('Professional'), while the catalog is keyed by billing-package
 * CODE FAMILY ('analytics'). Everything below maps one to the other and
 * degrades to a neutral, still-renderable state for a name it does not know —
 * the old template silently dropped its entire explanatory block for any value
 * other than the two it hardcoded.
 */
import {
  CatalogEntry,
  CatalogFeature,
  getCatalogEntry,
  MAX_FEATURE_LINES,
} from '@/config/onboardPricingCatalog';

/** lockInfo display name -> the billing-package code family getCatalogEntry() understands. */
const PLAN_CODE_BY_NAME: Record<string, string> = {
  'professional': 'analytics',
  'analytics': 'analytics',
  'replenishment': 'replenishment',
  'replenishment center': 'replenishment',
};

/**
 * The name a merchant actually sees on /subscription. `lockInfo` abbreviates
 * Replenishment Center to 'Replenishment'; showing that short form here and the
 * full one on the pricing page reads like two different plans.
 */
const PLAN_DISPLAY_NAME: Record<string, string> = {
  'professional': 'Professional',
  'analytics': 'Professional',
  'replenishment': 'Replenishment Center',
  'replenishment center': 'Replenishment Center',
};

const normalize = (requiredPackage: string): string =>
  (requiredPackage || '').trim().toLowerCase();

/** Catalog entry for a plan display name, or null when the name is unknown. */
export function planCatalogEntry(requiredPackage: string): CatalogEntry | null {
  const code = PLAN_CODE_BY_NAME[normalize(requiredPackage)];
  return code ? getCatalogEntry(code) : null;
}

/** Full plan name as /subscription spells it; unknown names pass through unchanged. */
export function planDisplayName(requiredPackage: string): string {
  const key = normalize(requiredPackage);
  return PLAN_DISPLAY_NAME[key] || (requiredPackage || '').trim();
}

/** Uppercase pill text, mirroring the `.op-badge` pills on the pricing cards. */
export function planBadgeLabel(requiredPackage: string): string {
  const name = planDisplayName(requiredPackage);
  return (name ? `${name} plan` : 'Premium plan').toUpperCase();
}

export function upgradeHeadline(featureName: string, requiredPackage: string): string {
  const name = planDisplayName(requiredPackage);
  const plan = name ? `the ${name} plan` : 'a paid plan';
  const feature = (featureName || '').trim();
  if (!feature) { return `Unlock this feature with ${plan}`; }
  // The v3 upsell section is TITLED after the plan it sells ('Replenishment
  // Center'), which would otherwise read 'Unlock Replenishment Center with the
  // Replenishment Center plan'.
  if (normalize(feature) === normalize(name)) { return `Unlock ${plan}`; }
  return `Unlock ${feature} with ${plan}`;
}

/**
 * One line under the headline: the menu item's own `tooltipText` when it has
 * one (it is specific to the feature the merchant just clicked), otherwise the
 * plan's catalog tagline — the same sentence the pricing card shows.
 */
export function upgradeTagline(description: string, requiredPackage: string): string {
  const own = (description || '').trim();
  if (own) { return own; }
  const entry = planCatalogEntry(requiredPackage);
  return entry ? entry.tagline : '';
}

/**
 * Feature list. Menu-supplied benefits win — they name what THIS locked item
 * does — and fall back to the plan's catalog features so a lockInfo without a
 * benefits array still shows the merchant what they would be buying. Capped at
 * the same MAX_FEATURE_LINES the pricing cards use.
 */
export function upgradeFeatures(
  benefits: string[],
  requiredPackage: string,
): CatalogFeature[] {
  const own = (benefits || []).map((text) => (text || '').trim()).filter((text) => text !== '');
  if (own.length > 0) {
    return own.slice(0, MAX_FEATURE_LINES).map((text) => ({ text }));
  }
  const entry = planCatalogEntry(requiredPackage);
  return entry ? entry.features.slice(0, MAX_FEATURE_LINES) : [];
}

/**
 * The catalog's "Everything in Professional, plus" lead-in belongs ONLY above
 * catalog features. Printing it above feature-specific menu benefits would
 * claim those four bullets are the whole delta between two plans.
 */
export function upgradeIntroLine(benefits: string[], requiredPackage: string): string {
  const hasOwnBenefits = (benefits || []).some((text) => (text || '').trim() !== '');
  if (hasOwnBenefits) { return ''; }
  const entry = planCatalogEntry(requiredPackage);
  return entry ? entry.introLine : '';
}

/** Primary CTA. It routes to /subscription, so it says so. */
export function upgradeCtaLabel(requiredPackage: string): string {
  const name = planDisplayName(requiredPackage);
  return name ? `View ${name} plans` : 'View plans';
}
